import React from "react";
import classnames from "classnames";
import { useTranslation } from "@helpers/translate";
import { LogoIcon } from "@components/library";
import { formatUsd } from "../../../../Controls/TradeForm/utils/format";
import { formatSignedPnl, getTone } from "../../../../Orders/positionFormat";
import { DetailRow } from "../../../components/DetailRow";
import { MULTIPLIER_SCALE_BARS, MULTIPLIER_SCALE_MAX } from "../../constants";
import { formatMultiplier } from "../../utils/tradeMath";
import "./TradeSummary.sass";

const Row = (props) => <DetailRow block="trade-summary" {...props} />;

/** Filled bars of the 0x … MULTIPLIER_SCALE_MAX scale */
const getFilledBars = (multiplier) => {
  if (multiplier == null) return 0;

  const share = Math.min(multiplier, MULTIPLIER_SCALE_MAX) / MULTIPLIER_SCALE_MAX;

  return Math.round(share * MULTIPLIER_SCALE_BARS);
};

/**
 * A trade too short for a chart: the profit, the multiplier on a scale and
 * the trade's terms.
 *
 * @param {object} props
 * @param {import("../../utils/tradeModel").TradeDetails} props.trade
 * @param {number|null} props.pnl
 * @param {number|null} props.multiplier
 * @param {string|null} props.timeHeld
 */
export const TradeSummary = ({ trade, pnl, multiplier, timeHeld }) => {
  const { __ } = useTranslation();
  const tone = getTone(pnl);
  const filledBars = getFilledBars(multiplier);

  return (
    <div className="trade-summary">
      <p className="trade-summary__label">
        {__("futures.trade_details.profit")}
      </p>
      <p
        className={classnames("trade-summary__values", {
          [`trade-summary__values--${tone}`]: tone,
        })}
      >
        <span className="trade-summary__pnl">{formatSignedPnl(pnl)}</span>
        <span className="trade-summary__multiplier">
          {formatMultiplier(multiplier)}
        </span>
      </p>
      {trade.isDemo && (
        <span className="trade-summary__demo">
          {__("futures.trade_details.demo_account")}
        </span>
      )}

      <div className="trade-summary__scale" aria-hidden="true">
        <div className="trade-summary__bars">
          {Array.from({ length: MULTIPLIER_SCALE_BARS }, (_, index) => (
            <span
              key={index}
              className={classnames("trade-summary__bar", {
                [`trade-summary__bar--${tone ?? "up"}`]: index < filledBars,
              })}
            />
          ))}
        </div>
        <div className="trade-summary__scale-labels">
          <span>0x</span>
          <span>{MULTIPLIER_SCALE_MAX}x</span>
        </div>
      </div>

      <div className="trade-summary__logo">
        <LogoIcon forceFullLogo />
      </div>

      <div className="trade-summary__rows">
        <Row label={__("futures.community.wager")}>
          {trade.wager != null ? formatUsd(trade.wager) : "—"}
        </Row>
        <Row label={__("futures.leverage")}>
          <span className="trade-summary__accent">
            {trade.leverage != null ? `${trade.leverage}x` : "—"}
          </span>
        </Row>
        <Row label={__("futures.trade_details.direction")}>
          {trade.isLong
            ? __("futures.trade_details.up")
            : __("futures.trade_details.down")}
        </Row>
        <Row label={__("futures.trade_details.time_held")}>
          {timeHeld ?? "—"}
        </Row>
      </div>
    </div>
  );
};
